/**
 * Small accessors for schedule domain objects: bar text for the chip label and aspect →
 * block id for grouping rows (blocks carry job/scope; aspects carry date + workers).
 */

import {
  toLocalCalendarDay,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import { sameScheduleBlockId } from "./scheduleIds";

/**
 * Bar label: block `notes` if set, otherwise nested `scope.name`, else catalog lookup.
 * @param {Object} block
 * @param {Map<string, Object>} [workScopeById]
 * @returns {string}
 */
export function getBlockLabel(block, workScopeById) {
  const fromNotes = typeof block.notes === "string" ? block.notes.trim() : "";
  if (fromNotes) {
    return fromNotes;
  }
  if (block.scope?.name) {
    return block.scope.name;
  }
  const sid = block.scope?.id ?? null;
  if (sid && workScopeById?.has(sid)) {
    return workScopeById.get(sid).name;
  }
  return sid || "Scope";
}

/**
 * @param {Object} aspect Per-day aspect row.
 * @returns {string|number|null} Parent block id.
 */
export function getAspectBlockId(aspect) {
  return aspect.workScopeAspectsBlock?.id ?? null;
}

/**
 * True when the per-day row exists on the server (matches checks in crew / paste flows).
 *
 * @param {Object|null|undefined} aspect
 * @returns {boolean}
 */
export function isPersistedScheduleAspect(aspect) {
  return aspect?.id != null && /^\d+$/.test(String(aspect.id));
}

/**
 * Stable key for one aspect row (persisted id, or synthetic key for optimistic placeholders).
 *
 * @param {Object} aspect
 * @returns {string}
 */
export function getScheduleAspectStableKey(aspect) {
  if (isPersistedScheduleAspect(aspect)) {
    return String(aspect.id);
  }
  const bid = getAspectBlockId(aspect) ?? "unknown";
  const dk = aspect?.date != null ? toLocalDateKey(aspect.date) : "nodate";
  return `temp:${bid}:${dk}`;
}

/**
 * Sorted unique calendar keys (`YYYY-MM-DD`) from aspect `date` fields.
 *
 * @param {Array<{ date?: Date }>|null|undefined} aspects
 * @returns {string[]}
 */
export function sortedUniqueDateKeysFromScheduleAspects(aspects) {
  const keys = new Set();
  for (const a of aspects ?? []) {
    if (a?.date != null) {
      keys.add(toLocalDateKey(a.date));
    }
  }
  return [...keys].sort();
}

/**
 * Filter and date-sort all per-day rows that belong to one block.
 *
 * @param {Array<Object>} aspects
 * @param {unknown} blockId
 * @returns {Array<Object>}
 */
export function getSortedScheduleAspectsForBlock(aspects, blockId) {
  return (aspects ?? [])
    .filter((aspect) => sameScheduleBlockId(getAspectBlockId(aspect), blockId))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Find one block-owned aspect row for a specific local calendar day.
 *
 * @param {Array<Object>} aspects
 * @param {unknown} blockId
 * @param {Date|string|number} day
 * @returns {Object|undefined}
 */
export function getScheduleAspectForBlockOnDate(aspects, blockId, day) {
  const dayKey = toLocalDateKey(day);
  return (aspects ?? []).find(
    (aspect) =>
      sameScheduleBlockId(getAspectBlockId(aspect), blockId) &&
      toLocalDateKey(aspect.date) === dayKey
  );
}

/**
 * True if any calendar day in the block's inclusive `[startDate, endDate]` span has
 * `DayDetails.locked` (via `scheduleDayLocks[YYYY-MM-DD]` from the day-details fetch).
 *
 * @param {Object|null|undefined} block
 * @param {Record<string, boolean>|null|undefined} dayLocks
 * @returns {boolean}
 */
export function blockDateSpanHasLockedDay(block, dayLocks) {
  if (!block?.startDate || !block?.endDate || dayLocks == null) {
    return false;
  }
  const start = toLocalCalendarDay(block.startDate);
  const end = toLocalCalendarDay(block.endDate);
  const cursor = new Date(start);
  while (cursor <= end) {
    if (dayLocks[toLocalDateKey(cursor)]) {
      return true;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return false;
}

/**
 * True if any `dispatch_modified` aspect exists in the searched inclusive date span.
 *
 * Reads `block.aspects` (per-day rows with `dispatch_modified` and `date`). Callers may
 * pass a synthetic block, e.g. `{ ...block, aspects: aspectsForSpan }`, to check only a
 * resize or move window.
 *
 * By default, the searched span is the block's own inclusive `[startDate, endDate]`.
 * `dateSpanStart` / `dateSpanEnd` narrow the window (missing edges fall back to block span).
 *
 * @param {Object|null|undefined} block Must include `aspects`, `startDate`, and `endDate` when used.
 * @param {Date|string|number|null} [dateSpanStart=null]
 * @param {Date|string|number|null} [dateSpanEnd=null]
 * @returns {boolean}
 */
export function blockDateSpanHasDispatchEdited(
  block,
  dateSpanStart = null,
  dateSpanEnd = null
) {
  const aspects = Array.isArray(block?.aspects) ? block.aspects : [];
  if (!aspects.length) {
    return false;
  }
  const rawStart = dateSpanStart ?? block?.startDate;
  const rawEnd = dateSpanEnd ?? block?.endDate;
  if (rawStart == null || rawEnd == null) {
    return false;
  }
  const start = toLocalCalendarDay(rawStart);
  const end = toLocalCalendarDay(rawEnd);
  const startKey = toLocalDateKey(start);
  const endKey = toLocalDateKey(end);
  const minKey = startKey <= endKey ? startKey : endKey;
  const maxKey = startKey <= endKey ? endKey : startKey;

  for (const aspect of aspects) {
    if (!aspect?.dispatch_modified || aspect.date == null) {
      continue;
    }
    const dayKey = toLocalDateKey(aspect.date);
    if (minKey <= dayKey && dayKey <= maxKey) {
      return true;
    }
  }
  return false;
}

/**
 * Sets each block's `contains_locked`: **true** if any day in the block's date span is
 * locked in Dispatch (`DayDetails.locked`). Use as a guard to block altering that block.
 *
 * `dayLocks` only has entries for dates loaded in {@link useScheduleController}'s
 * `scheduleDayLocks` (currently the visible timeline range); other dates in the span are
 * treated as unlocked unless present in the map.
 *
 * @param {Array<Object>|null|undefined} blocks
 * @param {Record<string, boolean>|null|undefined} dayLocks `scheduleDayLocks` shape
 * @returns {Array<Object>}
 */
export function mergeBlocksContainsLocked(blocks, dayLocks) {
  return (blocks ?? []).map((b) => ({
    ...b,
    contains_locked: blockDateSpanHasLockedDay(b, dayLocks),
  }));
}
